import { z } from 'zod';

/** App Router admin chrome — church switcher (legacy AdminNav parity). */
export const adminSchemas = {
  /** JWT church claim flipped; cookie written; hard reload follows. */
  'admin.church_switched': z.object({
    fromChurchId: z.string(),
    toChurchId: z.string(),
  }),
  /** Membership/super check failed or mutation rejected. */
  'admin.church_switch_failed': z.object({
    reason: z.enum(['forbidden', 'unauthenticated', 'unexpected']),
    toChurchId: z.string().optional(),
  }),
  /** Paying church clicked through the in-app referral nudge to /admin/referrals. */
  'admin.referral_nudge_clicked': z.object({
    churchId: z.string(),
  }),
  /** Paying church dismissed the in-app referral nudge (returns after a cooldown). */
  'admin.referral_nudge_dismissed': z.object({
    churchId: z.string(),
  }),
  /**
   * The in-app What's new panel rendered a release the viewer had not seen.
   *
   * This is the reach denominator for the weekly note. The Customer.io digest
   * reports its own opens, but most churches read the product, not the email,
   * so without this half we would judge the whole release-notes channel on the
   * email open rate alone and conclude nobody reads it.
   */
  'admin.whats_new_shown': z.object({
    churchId: z.string(),
    /** ISO date of the release being shown, so reach is per-batch. */
    release: z.string(),
  }),
  /** Viewer clicked through the What's new panel to the full changelog. */
  'admin.whats_new_opened': z.object({
    churchId: z.string(),
    release: z.string(),
  }),
  /** Viewer dismissed the panel. Returns only when a NEWER release lands. */
  'admin.whats_new_dismissed': z.object({
    churchId: z.string(),
    release: z.string(),
  }),
  /** Branding form autosave succeeded (colors / styles / assets). */
  'admin.branding_saved': z.object({
    churchId: z.string(),
  }),
  /** Branding Save failed (upload or mutation). */
  'admin.branding_save_failed': z.object({
    churchId: z.string().optional(),
    reason: z.enum(['upload', 'mutation', 'unexpected']),
  }),
  /**
   * Staff pressed Print on a guest's page and window.print() was called.
   * Whether a dialog opened, or they printed or cancelled, is not
   * observable. `scope`: the whole guest, or one class check-in.
   */
  'admin.guest_print_clicked': z.object({
    guestId: z.string(),
    scope: z.enum(['all_check_ins', 'one_check_in']),
  }),
  /** Ministry create succeeded. */
  'admin.ministry_created': z.object({
    churchId: z.string(),
    ministryId: z.string(),
  }),
  /** Ministry detail Save succeeded (name / description / type links). */
  'admin.ministry_saved': z.object({
    churchId: z.string(),
    ministryId: z.string(),
  }),
  /** Ministry Save / create / delete failed. */
  'admin.ministry_save_failed': z.object({
    churchId: z.string().optional(),
    ministryId: z.string().optional(),
    reason: z.enum(['create', 'mutation', 'delete', 'unexpected']),
  }),
  /** Ministry deleted. */
  'admin.ministry_deleted': z.object({
    churchId: z.string(),
    ministryId: z.string(),
  }),
  /** Profile name Save succeeded. */
  'admin.profile_name_saved': z.object({
    userId: z.string(),
  }),
  /** Profile email Save succeeded. */
  'admin.profile_email_saved': z.object({
    userId: z.string(),
  }),
  /** Profile password Save succeeded. */
  'admin.profile_password_saved': z.object({
    userId: z.string(),
  }),
  /** Profile Save failed. */
  'admin.profile_save_failed': z.object({
    reason: z.enum(['name', 'email', 'password', 'unexpected']),
    userId: z.string().optional(),
  }),
  /** Team member invite succeeded. */
  'admin.team_member_invited': z.object({
    churchId: z.string(),
    invitedUserId: z.string().optional(),
  }),
  /** Team ownership transferred to another member. */
  'admin.team_ownership_transferred': z.object({
    churchId: z.string(),
    toUserId: z.string(),
  }),
  /** Team member removed. */
  'admin.team_member_removed': z.object({
    churchId: z.string(),
    userId: z.string(),
  }),
  /** Team invite failed. */
  'admin.team_invite_failed': z.object({
    churchId: z.string().optional(),
    reason: z.enum(['forbidden', 'mutation', 'unexpected']),
  }),
  /**
   * Ownership transfer or member removal failed. These are the two destructive
   * team actions; without this branch a rejected mutation just closed the
   * confirm dialog and vanished (ADR 0002 — instrument the failure branches).
   */
  'admin.team_member_action_failed': z.object({
    action: z.enum(['transfer_ownership', 'remove_member']),
    churchId: z.string().optional(),
    message: z.string().optional(),
    userId: z.string(),
  }),
  /** Referral link copied to clipboard. */
  'admin.referral_link_copied': z.object({
    code: z.string(),
  }),
  /**
   * A referral code was minted WITHOUT the ActiveCampaign / Customer.io sync,
   * because the legacy mint op was unreachable. Permanent and unrecoverable on
   * its own: the legacy minter early-returns on an existing code, so it never
   * re-syncs — `{{ customer.referral_url }}` stays empty for this user until
   * someone backfills it. Emitted so the gap is visible instead of silent
   * (ADR 0002); `userId` is what a backfill would key off.
   *
   * `userId` is the remediation key, NOT the identity — `distinctIdFor`
   * does not read it, deliberately (see distinctId.ts: every rung there
   * exists for a subject that has no church at all). This emits from a
   * `protectedProcedure` with a verified church in hand, so it carries
   * `churchId` and groups with that church's other server events instead
   * of collapsing onto `'unknown'`.
   */
  'admin.referral_code_minted_unsynced': z.object({
    churchId: z.string(),
    reason: z.enum(['legacy_mint_failed']),
    userId: z.string(),
  }),
  /**
   * Staff approved an affiliate application and minted their code (ADR 0012).
   *
   * The one moment an affiliate becomes able to earn anything. Worth an event
   * because approval is manual and irreversible-ish — the code goes out in an
   * email and starts appearing in other people's marketing — so "when did this
   * one start" needs an answer that does not depend on someone remembering.
   */
  'admin.affiliate_approved': z.object({
    affiliateId: z.string(),
    approvedByUserId: z.string(),
    code: z.string(),
  }),
  /**
   * The approval email carrying an affiliate their link either went or did not.
   *
   * `sent: false` with a reason is the load-bearing half. An approved affiliate
   * who never receives their link has a code they have never seen, which from
   * their side is indistinguishable from never being approved — and from ours,
   * without this event, indistinguishable from an affiliate who was sent one
   * and did nothing. `not_configured` says the mailer has no message id yet and
   * a human must send it; `suppressed` is crmGuards refusing to mail from a
   * preview; `send_failed` is Customer.io.
   */
  'admin.affiliate_approval_email': z.object({
    affiliateId: z.string(),
    reason: z.enum(['not_configured', 'suppressed', 'send_failed']).optional(),
    sent: z.boolean(),
  }),
  /**
   * Staff recorded an affiliate payout. Money left the company for someone
   * outside it, so this is the audit trail's product-side half — the transfer
   * itself lives in the payment tool, by design.
   */
  'admin.affiliate_payout_recorded': z.object({
    affiliateId: z.string(),
    amountCents: z.number().int().nonnegative(),
    commissionCount: z.number().int().nonnegative(),
    paidByUserId: z.string(),
  }),
  /** Church settings Save succeeded (name / url / timezone / live chat). */
  'admin.church_settings_saved': z.object({
    churchId: z.string(),
  }),
  /** Church settings Save failed. */
  'admin.church_settings_save_failed': z.object({
    churchId: z.string().optional(),
    reason: z.enum(['mutation', 'conflict', 'unexpected']),
  }),
  /** Planning Center OAuth popup/redirect started. */
  'admin.pco_connect_started': z.object({
    churchId: z.string(),
  }),
  /** Planning Center OAuth URL fetch failed. */
  'admin.pco_connect_failed': z.object({
    churchId: z.string().optional(),
    message: z.string().optional(),
  }),
  /** Planning Center integration deleted. */
  'admin.pco_disconnected': z.object({
    churchId: z.string(),
  }),
  /** Planning Center cachedData refresh enqueued. */
  'admin.pco_refresh_started': z.object({
    churchId: z.string(),
  }),
  /** Planning Center refresh enqueue / poll failed. */
  'admin.pco_refresh_failed': z.object({
    churchId: z.string().optional(),
    message: z.string().optional(),
  }),
  /** Reports generate succeeded. */
  'admin.report_generated': z.object({
    churchId: z.string().optional(),
    conditionCount: z.number().int().nonnegative(),
    resultCount: z.number().int().nonnegative(),
  }),
  /** Reports generate failed. */
  'admin.report_generate_failed': z.object({
    reason: z.enum(['mutation', 'unexpected']),
  }),
};

/** Keep the pre-catalog PostHog insights wired. */
export const ADMIN_LEGACY_POSTHOG_NAMES: Readonly<
  Partial<Record<keyof typeof adminSchemas, string>>
> = {
  'admin.branding_saved': 'saveBrandingData',
  'admin.church_settings_saved': 'saveChurchSettings',
  'admin.church_switched': 'switchChurches',
  'admin.ministry_created': 'addMinistry',
  'admin.ministry_deleted': 'deleteMinistry',
  'admin.ministry_saved': 'saveMinistryData',
  'admin.pco_connect_failed': 'connectPCOError',
  'admin.pco_connect_started': 'connectPCO',
  'admin.pco_disconnected': 'disconnectPCO',
  'admin.pco_refresh_failed': 'refreshDataError',
  'admin.pco_refresh_started': 'refrestData',
  'admin.profile_email_saved': 'saveNewEmail',
  'admin.profile_name_saved': 'saveNewName',
  'admin.profile_password_saved': 'saveNewPassword',
  'admin.referral_link_copied': 'referralLinkCopied',
  'admin.team_member_invited': 'newTeamMember',
  'admin.team_member_removed': 'deleteUserFromTeam',
  'admin.team_ownership_transferred': 'transferOwnership',
};
